import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { resolveCurrentUserId } from "@/lib/articulos.server";

const TipoEnum = z.enum(["demandante", "anunciante", "agencia", "curioso"]);
const PerfilEnum = z.enum([
  "particular",
  "empresario",
  "franquicia",
  "prefiere_no_decirlo",
]);

const Input = z.object({
  tipo: TipoEnum,
  perfil: PerfilEnum.optional(),
});

export const saveOnboarding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => Input.parse(data))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Encontrar (o esperar) el contacto creado por el trigger handle_new_user
    const { data: contacto } = await supabaseAdmin
      .from("contactos")
      .select("id")
      .eq("user_id", context.userId)
      .maybeSingle();

    if (!contacto) {
      throw new Error("Contacto no encontrado para el usuario");
    }

    // Guardar perfil_emprendedor si aplica
    if (data.perfil) {
      await supabaseAdmin
        .from("contactos")
        .update({ perfil_emprendedor: data.perfil })
        .eq("id", contacto.id);
    }

    // Añadir tipo (idempotente)
    await supabaseAdmin
      .from("contacto_tipos")
      .upsert(
        { contacto_id: contacto.id, tipo: data.tipo },
        { onConflict: "contacto_id,tipo", ignoreDuplicates: true },
      );

    return { ok: true };
  });

export const countMyAnuncios = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { count } = await supabaseAdmin
      .from("anuncios")
      .select("id", { count: "exact", head: true })
      .eq("created_by", context.userId);
    return { count: count ?? 0 };
  });

// Devuelve si el usuario ya completó el onboarding (tiene al menos un tipo asignado).
export const getOnboardingStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: contacto } = await supabaseAdmin
      .from("contactos")
      .select("id")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!contacto) return { completed: false as const };
    const { count } = await supabaseAdmin
      .from("contacto_tipos")
      .select("contacto_id", { count: "exact", head: true })
      .eq("contacto_id", contacto.id);
    return { completed: (count ?? 0) > 0 };
  });

// Para usuarios recién registrados que aún no han confirmado el email
// (no hay sesión). Busca el contacto por email — el trigger handle_new_user
// ya lo creó al insertarse el usuario en auth.users.
const ByEmailInput = z.object({
  email: z.string().email(),
  tipo: TipoEnum,
  perfil: PerfilEnum.optional(),
});

export const saveOnboardingByEmail = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => ByEmailInput.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: contacto } = await supabaseAdmin
      .from("contactos")
      .select("id")
      .ilike("email", data.email)
      // El contacto vinculado a la cuenta recién creada (conserva su origen,
      // p. ej. "conector_light"; ya no se sobrescribe con "registro").
      .not("user_id", "is", null)
      .maybeSingle();

    if (!contacto) return { ok: false as const };

    // Solo permitido si aún NO ha completado el onboarding (sin tipos asignados).
    // De lo contrario, cualquiera podría sobrescribir perfil/tipo conociendo el email.
    const { count: tiposCount } = await supabaseAdmin
      .from("contacto_tipos")
      .select("contacto_id", { count: "exact", head: true })
      .eq("contacto_id", contacto.id);
    if ((tiposCount ?? 0) > 0) return { ok: false as const };

    if (data.perfil) {
      await supabaseAdmin
        .from("contactos")
        .update({ perfil_emprendedor: data.perfil })
        .eq("id", contacto.id);
    }

    await supabaseAdmin
      .from("contacto_tipos")
      .upsert(
        { contacto_id: contacto.id, tipo: data.tipo },
        { onConflict: "contacto_id,tipo", ignoreDuplicates: true },
      );

    return { ok: true as const };
  });

// Devuelve el perfil_emprendedor del usuario actual (o null si no hay sesión / contacto).
export const getMyPerfilEmprendedor = createServerFn({ method: "GET" }).handler(
  async () => {
    const userId = await resolveCurrentUserId();
    if (!userId) return { perfil: null as string | null };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("contactos")
      .select("perfil_emprendedor")
      .eq("user_id", userId)
      .maybeSingle();
    return { perfil: (data?.perfil_emprendedor ?? null) as string | null };
  },
);

// ───────── Onboarding hint flags (JSON extensible) ─────────
const OnboardingFlagKey = z.enum([
  "demanda_badge_hint_seen",
  "anuncios_like_hint_seen",
]);

export type OnboardingFlags = Partial<Record<z.infer<typeof OnboardingFlagKey>, boolean>>;

export const getOnboardingFlags = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<OnboardingFlags> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("contactos")
      .select("onboarding_flags")
      .eq("user_id", context.userId)
      .maybeSingle();
    const raw = (data as { onboarding_flags?: unknown } | null)?.onboarding_flags;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      return raw as OnboardingFlags;
    }
    return {};
  });

export const setOnboardingFlag = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ key: OnboardingFlagKey, value: z.boolean() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("contactos")
      .select("id, onboarding_flags")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!row) return { ok: false as const };
    const current =
      row.onboarding_flags && typeof row.onboarding_flags === "object" && !Array.isArray(row.onboarding_flags)
        ? (row.onboarding_flags as OnboardingFlags)
        : {};
    const next = { ...current, [data.key]: data.value };
    const { error } = await supabaseAdmin
      .from("contactos")
      .update({ onboarding_flags: next })
      .eq("id", row.id);
    if (error) throw new Error(error.message);
    return { ok: true as const, flags: next };
  });
